import { cva, type VariantProps } from "class-variance-authority";
import { type ElementType, forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

const styles = cva("leading-none", {
	variants: {
		variant: {
			// Display — GT Ultra Median Thin, uppercase
			"heading-display-xl": "font-display-thin text-8xl uppercase",
			"heading-display-lg": "font-display-thin text-6xl uppercase",
			"heading-display-md": "font-display-thin text-5xl uppercase",
			"heading-display-sm": "font-display-thin text-2xl uppercase",
			"heading-display-xs": "font-display-thin text-lg uppercase",
			// Heading — GT Ultra Median, weight 300
			"heading-3xl": "font-display font-light text-5xl",
			"heading-2xl": "font-display font-light text-4xl",
			"heading-xl": "font-display font-light text-3xl",
			"heading-lg": "font-display font-light text-2xl",
			"heading-md": "font-display font-light text-lg",
			// Number — GT Standard, weight 700, tabular lining figures
			"number-3xl": "font-body font-bold text-5xl tabular-nums lining-nums",
			"number-2xl": "font-body font-bold text-4xl tabular-nums lining-nums",
			"number-xl": "font-body font-bold text-3xl tabular-nums lining-nums",
			"number-lg": "font-body font-bold text-2xl tabular-nums lining-nums",
			"number-md": "font-body font-bold text-xl tabular-nums lining-nums",
			// Body Regular — GT Standard, weight 400
			"body-lg-reg": "font-body font-regular text-lg tracking-[-0.025em]",
			"body-md-reg": "font-body font-regular text-base tracking-[-0.025em]",
			"body-sm-reg": "font-body font-regular text-sm",
			"body-xs-reg": "font-body font-regular text-xs tracking-[-0.025em]",
			// Body Bold — GT Standard, weight 700
			"body-lg-bold": "font-body font-bold text-lg tracking-[-0.025em]",
			"body-md-bold": "font-body font-bold text-base tracking-[-0.025em]",
			"body-sm-bold": "font-body font-bold text-sm tracking-[-0.025em]",
			"body-xs-bold": "font-body font-bold text-xs tracking-[-0.025em]",
			// Label Regular — GT Standard, weight 400, uppercase, tabular lining
			"label-xl-reg": "font-body font-regular text-lg uppercase tabular-nums lining-nums",
			"label-lg-reg": "font-body font-regular text-base uppercase tabular-nums lining-nums",
			"label-md-reg": "font-body font-regular text-sm uppercase tabular-nums lining-nums",
			"label-sm-reg": "font-body font-regular text-xs uppercase tabular-nums lining-nums",
			// Label Bold — GT Standard, weight 700, uppercase, tabular lining
			"label-xl-bold": "font-body font-bold text-lg uppercase tabular-nums lining-nums",
			"label-lg-bold": "font-body font-bold text-base uppercase tabular-nums lining-nums",
			"label-md-bold": "font-body font-bold text-sm uppercase tabular-nums lining-nums",
			"label-sm-bold": "font-body font-bold text-xs uppercase tabular-nums lining-nums",
			// Others
			"eyebrow-md": "font-body font-regular text-sm",
			"eyebrow-sm": "font-body font-regular text-xs",
			"input-field": "font-system font-regular text-sm",
			"input-field-placeholder": "font-system font-regular text-xs",
			"text-mini-reg": "font-body font-regular text-2xs",
			"text-mini-bold": "font-body font-bold text-2xs",
			"original-price": "font-body font-regular text-xs line-through",
			"nav-item": "text-sm font-display font-light",
		},
	},
	defaultVariants: {
		variant: "body-md-reg",
	},
});

type Props = VariantProps<typeof styles> &
	HTMLAttributes<HTMLElement> & {
		as?: ElementType;
		children: ReactNode;
	};

export const Typography = forwardRef<HTMLElement, Props>(
	({ as: Component = "p", children, className, variant, ...rest }, ref) => {
		return (
			<Component ref={ref} className={twMerge(styles({ variant }), className)} {...rest}>
				{children}
			</Component>
		);
	},
);

Typography.displayName = "Typography";
